import { addError, clearUserForTracking, setUserForTracking } from '@infra/datadog/tracking';

import { base64UrlToUnicode } from '@libs/encoding';
import { formatName } from '@libs/formatNames';
import { getLocalStorage } from '@libs/storage';
import { Subscription } from '@libs/subscription';

/*
    ____,-------------------------------,____
    \   |            Version            |   /
    /___|-------------------------------|___\
*/

const CURRENT_VERSION = 5 as const;
const UPGRADABLE_VERSION = 4 as const;

/*
    ____,-------------------------------,____
    \   |             Types             |   /
    /___|-------------------------------|___\
*/

const TOKEN_CHECK_INTERVAL = 1 /* hour */ * 60 * 60 * 1000;

// считаем токен старым, если отавшееся время жизни меньше этого значения
const STALE_TOKEN_THRESHOLD = 15 /* days */ * 24 * 60 * 60 * 1000;

type AuthStatus = 'no-auth' | 'authed' | 'refresh';

interface Tokens {
  access: string;
  expAccess: number;
  iatAccess?: number;
  user: AuthServiceUser;

  ver: typeof CURRENT_VERSION;
}

interface JWTToken {
  id: string; // userId
  exp: number;
  iat: number;
}

export interface User {
  userId: string;
  globalId?: number | null;
  firstName: string;
  lastName: string;
  avatarSrc?: string;
  clubs: Array<{ clubId: string; role: 'Manager' | 'Member' }>;
  isAdmin?: boolean | null;
}

export interface ClubRoles {
  isManager: boolean;
}

/*
    ____,-------------------------------,____
    \   |             Service           |   /
    /___|-------------------------------|___\
*/

const LOCAL_STORAGE = getLocalStorage();

export class AuthService {
  #tokens?: Tokens;
  #subscription = new Subscription<AuthStatus>();
  #initialized = false;
  #timeout: null | ReturnType<typeof setTimeout> = null;

  private constructor() {
    this.syncFromLocalStorage();
    this.#initialized = true;
  }

  expiredInBackgound = false;

  get accessToken(): string | undefined {
    return this.#tokens?.access;
  }

  get user(): AuthServiceUser | undefined {
    return this.#tokens?.user;
  }

  get isAuthenticated(): boolean {
    return this.status !== 'no-auth';
  }

  get status(): AuthStatus {
    if (!this.#tokens?.access) return 'no-auth';

    // upgrade token
    if (this.#tokens.ver < CURRENT_VERSION) return 'refresh';

    const remaining = this.#tokens.expAccess - Date.now();

    if (remaining <= 1000) return 'no-auth';

    if (remaining <= STALE_TOKEN_THRESHOLD) return 'refresh';

    return 'authed';
  }

  syncFromLocalStorage() {
    const data = LOCAL_STORAGE.getItem('auth');

    if (!data) {
      if (this.#tokens) {
        this.deleteTokens();
      }
      return;
    }

    try {
      const obj = JSON.parse(data) as Tokens;

      if (obj.ver < UPGRADABLE_VERSION) {
        this.deleteTokens();
        return;
      }

      const isDifferent = !this.#tokens || obj.access !== this.#tokens.access;
      if (isDifferent) {
        this.#tokens = obj;
        this.#clearTokenExpiration();
        this.#startTokenExpiration();
        this.#trackUser();
        this.#publish();
      }
    } catch (e) {
      addError(
        new Error("AuthService: Can't setup session data from localStorage.", {
          cause: e,
        }),
      );
      this.deleteTokens();
    }
  }

  saveTokens(access: string, user: User): void {
    this.expiredInBackgound = false;

    const parsed = this.#parseJWT(access);
    const iatAccess = Date.now();
    const expAccess = parsed.exp * 1000;

    this.#tokens = {
      access,
      iatAccess,
      expAccess,
      user: new AuthServiceUser(user),
      ver: CURRENT_VERSION,
    };

    LOCAL_STORAGE.setItem('auth', JSON.stringify(this.#tokens));

    this.#clearTokenExpiration();
    this.#startTokenExpiration();
    this.#publish();
    // tracking
    this.#trackUser();
  }

  updateUserOnly(user: User): void {
    if (!this.#tokens || user.userId !== this.user?.userId) {
      return;
    }

    this.#tokens.user = new AuthServiceUser(user);
    LOCAL_STORAGE.setItem('auth', JSON.stringify(this.#tokens));
  }

  deleteTokens(): void {
    this.#tokens = undefined;
    LOCAL_STORAGE.removeItem('auth');

    this.#clearTokenExpiration();
    this.#publish();
    // tracking
    this.#untrackUser();
  }

  subscribe(cb: () => void): () => void {
    return this.#subscription.subscribe(cb);
  }

  socketAccessTokenFactory = () => {
    const token = this.accessToken;

    if (!token || !this.isAuthenticated) {
      throw new Error("Can't open socket: no valid access token");
    }

    return token;
  };

  #parseJWT(token: string): JWTToken {
    const payload = token.split('.')[1]!;
    const parsed = JSON.parse(base64UrlToUnicode(payload));
    return parsed;
  }

  #startTokenExpiration() {
    if (!this.#tokens) return;

    const prevStatus = this.status;

    // Токен протух фоном

    if (prevStatus === 'no-auth') {
      this.deleteTokens();
      this.expiredInBackgound = true;
      return;
    }

    // Токен есть, следим за временем жизни

    const delay = Math.min(Math.max(this.#tokens.expAccess - Date.now(), 0), TOKEN_CHECK_INTERVAL);

    this.#timeout = setTimeout(() => {
      this.#timeout = null;

      if (!this.#tokens) return;

      if (this.status === 'no-auth') {
        this.deleteTokens();
        return;
      }

      if (this.status !== prevStatus) {
        this.#publish();
      }

      this.#startTokenExpiration();
    }, delay);
  }

  #clearTokenExpiration() {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
  }

  #publish() {
    if (this.#initialized) this.#subscription.publish(this.status);
  }

  // Tracking

  #trackUser() {
    const user = this.user;
    if (user) {
      setUserForTracking(user.userId, formatName(user));
    }
  }

  #untrackUser() {
    clearUserForTracking();
  }

  // Statics

  static #instance: AuthService;

  static get instance(): AuthService {
    if (!this.#instance) {
      this.#instance = new AuthService();
    }

    return this.#instance;
  }
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

class AuthServiceUser {
  userId: string;
  globalId?: number | null;
  firstName: string;
  lastName: string;
  avatarSrc?: string;
  clubs: Record<string, ClubRoles> = {};
  isAdmin: boolean;

  constructor({ userId, globalId, firstName, lastName, avatarSrc, clubs, isAdmin }: User) {
    this.userId = userId;
    this.globalId = globalId;
    this.firstName = firstName;
    this.lastName = lastName;
    this.avatarSrc = avatarSrc;
    this.isAdmin = Boolean(isAdmin);

    clubs.reduce((sink, c) => {
      if (!sink[c.clubId]) {
        sink[c.clubId] = { isManager: false };
      }
      const r = sink[c.clubId]!;
      switch (c.role) {
        case 'Manager':
          r.isManager = true;
          break;
      }

      return sink;
    }, this.clubs);
  }
}
